import { Box, Button, Card, CardContent, CircularProgress, Stack, Typography } from '@mui/material'
import { observer } from 'mobx-react-lite'
import { useEffect, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import { useStore } from '@hooks/useStore'
import { URL_PATHS } from '@constants'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'

type VerificationStatus = 'loading' | 'success' | 'error'

export const EmailVerificationView = observer(() => {
  const { auth } = useStore()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [status, setStatus] = useState<VerificationStatus>('loading')
  const [errorMessage, setErrorMessage] = useState<string>('')

  const id = searchParams.get('id')
  const hash = searchParams.get('hash')

  useEffect(() => {
    const verifyEmail = async () => {
      if (!id || !hash) {
        setStatus('error')
        setErrorMessage('Ongeldige verificatie link. De vereiste parameters ontbreken.')
        return
      }

      try {
        await auth.verifyEmail(id, hash)
        setStatus('success')
        toast.success('Uw e-mailadres is succesvol geverifieerd!')
      } catch (error: any) {
        setStatus('error')
        const message =
          error.response?.data?.message || 'Verificatie mislukt. De link is mogelijk verlopen of ongeldig.'
        setErrorMessage(message)
        toast.error(message)
      }
    }

    verifyEmail()
  }, [id, hash, auth])

  const renderContent = () => {
    switch (status) {
      case 'loading':
        return (
          <Stack
            spacing={3}
            alignItems="center">
            <CircularProgress size={60} />
            <Typography
              variant="h5"
              align="center">
              E-mailadres Verifiëren
            </Typography>
            <Typography
              variant="body1"
              align="center"
              color="text.secondary">
              Een moment geduld terwijl we uw e-mailadres verifiëren...
            </Typography>
          </Stack>
        )

      case 'success':
        return (
          <Stack
            spacing={3}
            alignItems="center">
            <CheckCircleOutlineIcon sx={{ fontSize: 80, color: 'success.main' }} />
            <Typography
              variant="h5"
              align="center"
              color="success.main">
              E-mail Geverifieerd!
            </Typography>
            <Typography
              variant="body1"
              align="center">
              Uw e-mailadres is succesvol geverifieerd. U kunt nu inloggen en alle functies van uw account gebruiken.
            </Typography>
            <Button
              component={Link}
              to={auth.is_authenticated ? URL_PATHS.DASHBOARD : URL_PATHS.LOGIN}
              variant="contained"
              fullWidth>
              {auth.is_authenticated ? 'Ga naar Dashboard' : 'Inloggen'}
            </Button>
          </Stack>
        )

      case 'error':
        return (
          <Stack
            spacing={3}
            alignItems="center">
            <ErrorOutlineIcon sx={{ fontSize: 80, color: 'error.main' }} />
            <Typography
              variant="h5"
              align="center"
              color="error.main">
              Verificatie Mislukt
            </Typography>
            <Typography
              variant="body1"
              align="center">
              {errorMessage}
            </Typography>
            <Stack
              direction="row"
              spacing={2}
              width="100%">
              <Button
                component={Link}
                to={URL_PATHS.LOGIN}
                variant="outlined"
                fullWidth>
                Naar Inloggen
              </Button>
              <Button
                component={Link}
                to={URL_PATHS.SIGNUP}
                variant="contained"
                fullWidth>
                Opnieuw Registreren
              </Button>
            </Stack>
          </Stack>
        )
    }
  }

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        backgroundColor: 'background.default',
        padding: 2,
      }}>
      <Card sx={{ maxWidth: 500, width: '100%' }}>
        <CardContent sx={{ p: 4 }}>{renderContent()}</CardContent>
      </Card>
    </Box>
  )
})
